import { ChangeEvent, FC, useRef, useState } from 'react';
import { FileWarning, Loader2, UploadIcon, X } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Modal as BaseModal } from 'features/common/components/Modal';
import { OUTLINED, PRIMARY, QUATERNARY } from 'features/common/components/Button/constants';
import { TicketType } from 'features/Home/components/Events/types';
import api from 'services/api';
import { queryClient } from 'index';
import { useToastNotification } from 'hooks/useToastNotification';

import {
	FIXED_HEADERS,
	fileTypes,
	formatBytes,
	parseCsvData,
	parseExcelFile,
	processDataWithPlaceholderEmails,
	processMultiColumnData,
	processSingleColumnData,
} from './utils';
import { FileData, SendCompedTicketsModalProps } from './types';
import { ReorderableHeader } from './ReorderableHeader';
import * as Styled from './SendCompedTicketsModal.styles';

export const SendCompedTicketsModal: FC<SendCompedTicketsModalProps> = ({ ticketTypes, closeModal }) => {
	const { t } = useTranslation();
	const { showNotification, TYPES } = useToastNotification();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [fileData, setFileData] = useState<FileData | null>(null);
	const [selectedTicketType, setSelectedTicketType] = useState<TicketType | null>(null);
	const [reorderedHeaders, setReorderedHeaders] = useState<string[]>([...FIXED_HEADERS]);

	const moveHeader = (fromIndex: number, direction: 'left' | 'right') => {
		const newHeaders = [...reorderedHeaders];
		const toIndex = direction === 'left' ? fromIndex - 1 : fromIndex + 1;

		if (toIndex >= 0 && toIndex < newHeaders.length) {
			[newHeaders[fromIndex], newHeaders[toIndex]] = [newHeaders[toIndex], newHeaders[fromIndex]];
			setReorderedHeaders(newHeaders);
		}
	};

	const getTotalQuantity = (): number => {
		if (!fileData?.data) return 0;
		return fileData.data.reduce((total, row) => {
			const quantity = parseInt(row['Quantity'] || '0', 10);
			return total + (isNaN(quantity) ? 0 : quantity);
		}, 0);
	};

	const getAvailableTickets = (): number => {
		if (!selectedTicketType) return 0;
		return selectedTicketType.amount - selectedTicketType.sold;
	};

	const isQuantityExceeded = (): boolean => getTotalQuantity() > getAvailableTickets();

	const buildCsvContent = (): string => {
		if (!fileData?.data) return '';
		const csvRows = fileData.data.map((row) =>
			reorderedHeaders
				.map((header) => {
					if (header === 'Ticket Type' && selectedTicketType) return selectedTicketType.ID.toString();
					return row[header] || '';
				})
				.join(',')
		);
		return csvRows.join('\n');
	};

	const { mutate: handleSendTickets, isLoading: isSending } = useMutation({
		mutationFn: async () => {
			if (!fileData?.data || fileData.data.length === 0) {
				throw new Error('No data to send');
			}

			const csvContent = buildCsvContent();
			const blob = new Blob([csvContent], { type: 'text/csv' });
			const csvFile = new File([blob], 'comped_tickets.csv', { type: 'text/csv' });

			const formData = new FormData();
			formData.append('file', csvFile);

			const { data } = await api.post('/tickets/comp-tickets/csv', formData);
			return data;
		},
		onSuccess: () => {
			showNotification({ message: t('send-tickets-success'), type: TYPES.SUCCESS });
			queryClient.refetchQueries(['orders']);
			closeModal();
		},
		onError: (error) => {
			showNotification({ message: t('send-tickets-error'), type: TYPES.ERROR, error });
		},
	});

	const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		setFileData({
			file,
			data: [],
			headers: [],
			columnMapping: [0, 1, 2, 3, 4],
			isProcessing: true,
			errors: null,
		});

		try {
			let csv = '';
			if (file.type === fileTypes.excel || /\.(xlsx|xls)$/i.test(file.name)) {
				csv = await parseExcelFile(file);
			} else if (file.type === fileTypes.csv || /\.csv$/i.test(file.name)) {
				csv = await file.text();
			} else {
				setFileData((prev) => (prev ? { ...prev, errors: 'Invalid file type', isProcessing: false } : null));
				return;
			}

			const { data, headers } = await parseCsvData(csv);

			let processedData = data;
			if (headers.length === 1) {
				processedData = processSingleColumnData(data, headers);
			} else {
				processedData = processMultiColumnData(data, headers);
			}
			processedData = processDataWithPlaceholderEmails(processedData);

			setFileData({
				file,
				data: processedData,
				headers: [...FIXED_HEADERS],
				columnMapping: [0, 1, 2, 3, 4],
				isProcessing: false,
				errors: null,
			});
		} catch {
			setFileData((prev) => (prev ? { ...prev, errors: 'Error processing file', isProcessing: false } : null));
		}
	};

	const handleDeleteFile = () => {
		setFileData(null);
		setSelectedTicketType(null);
		setReorderedHeaders([...FIXED_HEADERS]);
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const handleDownloadCsv = () => {
		if (!fileData?.data || fileData.data.length === 0) return;
		const csvContent = buildCsvContent();
		const blob = new Blob([csvContent], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = 'comped_tickets_processed.csv';
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	};

	const handleSelectTicketType = (e: ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value;
		const ticketType = ticketTypes.find((tt) => tt.ID.toString() === value) || null;
		setSelectedTicketType(ticketType);
	};

	const sortedPreviewRows = (() => {
		if (!fileData?.data) return [];
		return fileData.data
			.slice()
			.sort((a, b) => {
				const qA = parseInt(a['Quantity'] || '1', 10);
				const qB = parseInt(b['Quantity'] || '1', 10);
				return qB - qA;
			})
			.slice(0, 10);
	})();

	return (
		<Styled.Modal onClose={closeModal} isWide>
			<BaseModal.Header>{t('send-tickets-title')}</BaseModal.Header>
			<Styled.Body>
				<Styled.Description>
					Upload a CSV or XLSX file with your data. For single-column files, we&apos;ll automatically detect emails or
					names and set quantity to 1. For multi-column files, drag and drop the column headers to map your data columns
					to the required format:
					<Styled.HeaderChips>
						{FIXED_HEADERS.map((header) => (
							<Styled.Chip key={header}>{header}</Styled.Chip>
						))}
					</Styled.HeaderChips>
					<Styled.Hint>
						<strong>Single-column support:</strong> Upload a list of emails or names like &ldquo;John Doe 2&rdquo;
						(quantity will be auto-detected).
					</Styled.Hint>
				</Styled.Description>

				{!fileData ? (
					<Styled.DropZone onClick={() => fileInputRef.current?.click()}>
						<Styled.UploadIconCircle>
							<UploadIcon size={24} />
						</Styled.UploadIconCircle>
						<Styled.DropZoneText>
							Click here to upload your file.
							<br />
							Supported file types: <strong>.csv</strong>, <strong>.xlsx</strong>, <strong>.xls</strong>.
						</Styled.DropZoneText>
						<Styled.ModalButton
							category={OUTLINED}
							type="button"
							text="Select file"
							onClick={(e) => {
								e.stopPropagation();
								fileInputRef.current?.click();
							}}
						/>
						<Styled.HiddenInput ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange} />
					</Styled.DropZone>
				) : (
					<Styled.FileBlock>
						<Styled.FileInfoRow>
							<Styled.FileMeta>
								<Styled.FileTexts>
									<Styled.FileName>
										{fileData.file.name}
										{!fileData.isProcessing && !fileData.errors && ` (${fileData.data.length} rows)`}
									</Styled.FileName>
									<Styled.FileSize>{formatBytes(fileData.file.size)}</Styled.FileSize>
								</Styled.FileTexts>
								{fileData.errors && <FileWarning size={16} color="#dc2626" />}
								{fileData.isProcessing && <Loader2 size={16} className="animate-spin" />}
							</Styled.FileMeta>
							<Styled.RemoveFileBtn type="button" onClick={handleDeleteFile} aria-label="Remove file">
								<X size={16} />
							</Styled.RemoveFileBtn>
						</Styled.FileInfoRow>

						{fileData.errors && (
							<Styled.ErrorBox>
								<p>{fileData.errors}</p>
							</Styled.ErrorBox>
						)}

						{fileData.data.length > 0 && (
							<>
								<Styled.FieldGroup>
									<Styled.FieldLabel htmlFor="ticket-type-select">Ticket Type</Styled.FieldLabel>
									<Styled.SelectInput
										id="ticket-type-select"
										value={selectedTicketType?.ID.toString() || ''}
										onChange={handleSelectTicketType}
									>
										<option value="" disabled>
											Select a ticket type
										</option>
										{ticketTypes.map((ticketType) => (
											<option key={ticketType.ID} value={ticketType.ID.toString()}>
												{ticketType.name} • Sold: {ticketType.sold}/{ticketType.amount}
											</option>
										))}
									</Styled.SelectInput>
								</Styled.FieldGroup>

								{selectedTicketType && isQuantityExceeded() && (
									<Styled.ErrorBox>
										<p>
											<strong>Warning:</strong> Total quantity ({getTotalQuantity()}) exceeds available tickets (
											{getAvailableTickets()}) for &ldquo;{selectedTicketType.name}&rdquo;.
										</p>
										<p>
											Please increase the ticket capacity, select a different ticket type, or reduce the number of
											tickets in your file.
										</p>
									</Styled.ErrorBox>
								)}

								<Styled.PreviewHeaderRow>
									<h3>Preview Data</h3>
									<p>Use the arrow buttons to reorder which file columns map to each required field</p>
								</Styled.PreviewHeaderRow>

								<Styled.TableWrapper>
									<Styled.PreviewTable>
										<thead>
											<tr>
												{reorderedHeaders.map((header, index) => (
													<ReorderableHeader
														key={header}
														header={header}
														index={index}
														onMoveLeft={() => moveHeader(index, 'left')}
														onMoveRight={() => moveHeader(index, 'right')}
														canMoveLeft={index > 0}
														canMoveRight={index < reorderedHeaders.length - 1}
													/>
												))}
											</tr>
										</thead>
										<tbody>
											{sortedPreviewRows.map((row, rowIndex) => (
												<tr key={rowIndex}>
													{reorderedHeaders.map((_header, colIndex) => {
														const dataKey = FIXED_HEADERS[colIndex];
														let value = row[dataKey] || '';
														if (dataKey === 'Ticket Type' && selectedTicketType) {
															value = selectedTicketType.name;
														}
														return (
															<Styled.DataCell key={colIndex}>
																<div>{value}</div>
															</Styled.DataCell>
														);
													})}
												</tr>
											))}
										</tbody>
									</Styled.PreviewTable>
								</Styled.TableWrapper>

								{fileData.data.length > 10 && (
									<Styled.TruncatedNote>
										Showing first 10 rows of {fileData.data.length} total rows
									</Styled.TruncatedNote>
								)}
							</>
						)}
					</Styled.FileBlock>
				)}
			</Styled.Body>
			<BaseModal.ActionButtons>
				<Styled.ActionsRow>
					<Styled.ModalButton category={QUATERNARY} type="button" onClick={closeModal} text="Cancel" />
					<Styled.ModalButton
						category={OUTLINED}
						type="button"
						onClick={handleDownloadCsv}
						disabled={!fileData?.data || fileData.data.length === 0}
						text="Download CSV"
					/>
					<Styled.ModalButton
						category={PRIMARY}
						type="button"
						onClick={() => handleSendTickets()}
						disabled={
							!fileData?.data || fileData.data.length === 0 || isSending || !selectedTicketType || isQuantityExceeded()
						}
						text={isSending ? 'Sending...' : `Send ${getTotalQuantity()} Tickets`}
					/>
					{isSending && <Styled.Spinner />}
				</Styled.ActionsRow>
			</BaseModal.ActionButtons>
		</Styled.Modal>
	);
};
